/**
 * One reader for "what is on this clinician's calendar right now": the server
 * snapshot with the optimistic overlay applied — moved visits repositioned,
 * canceled visits dropped, added visits and blocks appended, removed blocks
 * dropped, re-timed blocks moved. My week, the confirmation round and the
 * coverage drawer read this; the product's command center keeps its own poll.
 */
import type { BlockLike, DateKey, DemoState, VisitResourceLike } from "@caredna-marlin/utils/homeHealthDemo/state";
import { dateKeyStart, addDaysToKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { visitsOnDay } from "./confirmation";

type CalendarState = Pick<DemoState, "server" | "local" | "config"> & Partial<Pick<DemoState, "blockAddresses">>;

export interface EffectiveCalendar {
  visits: VisitResourceLike[];
  blocks: BlockLike[];
  /** Ids (visit ids, temp ids) that come from the overlay and are not yet confirmed by the server. */
  pendingKeys: string[];
}

export const TEMP_ID_BASE = -1_000_000;

function tempServerId(tempId: string): number {
  let h = 0;
  for (let i = 0; i < tempId.length; i++) h = (h * 31 + tempId.charCodeAt(i)) >>> 0;
  return TEMP_ID_BASE - (h % 1_000_000);
}


/** Every visit in the horizon with the overlay applied — the list every capacity number counts. */
export function effectiveVisitResources(state: Pick<DemoState, "server" | "local">): VisitResourceLike[] {
  const overlay = state.local?.visits;
  if (!overlay || Object.keys(overlay).length === 0) return state.server.visitResources;
  return state.server.visitResources.flatMap((vr) => {
    const change = overlay[String(vr.visitId)];
    if (!change) return [vr];
    if (change.canceled) return [];
    return [{ ...vr, providerId: change.providerId ?? vr.providerId, startIso: change.startIso, endIso: change.endIso }];
  });
}

/**
 * The day's visits with the overlay applied — what the coverage panel's unfilled
 * list and the call-out ranking read, so a visit moved from a row leaves the list
 * at the moment of the move rather than a poll later.
 */
export function effectiveVisitsOnDay(state: Pick<DemoState, "server" | "local">, dateKey: DateKey, timeZone: string): VisitResourceLike[] {
  return visitsOnDay({ ...state.server, visitResources: effectiveVisitResources(state) }, dateKey, timeZone);
}

export function selectEffectiveCalendar(state: CalendarState, providerId: number | undefined, range?: { start: DateKey; end: DateKey }): EffectiveCalendar {
  const tz = state.config.branch.timeZone;
  const from = range ? dateKeyStart(range.start, tz).getTime() : -Infinity;
  const to = range ? dateKeyStart(addDaysToKey(range.end, 1), tz).getTime() : Infinity;
  const inRange = (startIso: string) => {
    const t = new Date(startIso).getTime();
    return t >= from && t < to;
  };
  const pendingKeys: string[] = [];
  const visits: VisitResourceLike[] = [];
  const seen = new Set<number>();
  for (const vr of state.server.visitResources) {
    if (seen.has(vr.visitId)) continue;
    seen.add(vr.visitId);
    const change = state.local.visits[String(vr.visitId)];
    let effective = vr;
    if (change) {
      pendingKeys.push(`visit:${vr.visitId}`);
      if (change.canceled) continue;
      effective = { ...vr, providerId: change.providerId ?? vr.providerId, startIso: change.startIso, endIso: change.endIso };
    }
    if (effective.eventStatus === "canceled") continue;
    if (providerId !== undefined && effective.providerId !== providerId) continue;
    if (!inRange(effective.startIso)) continue;
    visits.push(effective);
  }
  for (const added of state.local.addedVisits) {
    if (providerId !== undefined && added.providerId !== providerId) continue;
    if (!inRange(added.startIso)) continue;
    pendingKeys.push(`added:${added.tempId}`);
    const id = tempServerId(added.tempId);
    visits.push({
      id,
      visitId: id,
      providerId: added.providerId,
      startIso: added.startIso,
      endIso: added.endIso,
      durationMinutes: Math.max(0, Math.round((new Date(added.endIso).getTime() - new Date(added.startIso).getTime()) / 60_000)),
      startOfShiftDriveMinutes: 0,
      interVisitDriveMinutes: 0,
      endOfShiftDriveMinutes: 0,
      visitTypeId: added.visitTypeId ?? -1,
      visitTypeName: added.visitTypeName,
      patientId: added.patientId ?? -1,
      patientName: added.patientName,
      eventStatus: "scheduled",
    });
  }
  // Keyed by block id, which survives the block being re-timed on My day.
  const addresses = state.blockAddresses ?? {};
  // A block she has just removed goes at click time; the overlay entry clears
  // when the horizon reads it canceled.
  const removed = state.local.canceledBlocks ?? {};
  for (const id of Object.keys(removed)) pendingKeys.push(`cancelBlock:${Number(id)}`);
  // A block she has just re-timed on My day sits at its new hour from the drop;
  // the entry clears when the horizon reads it there. The range filter is
  // applied after the move, so a block dragged into the day comes with it.
  const moved = state.local.movedBlocks ?? {};
  const blocks: BlockLike[] = state.server.blocks
    .filter((b) => !b.canceled && removed[String(b.id)] === undefined && (providerId === undefined || b.providerId === providerId))
    .map((b) => (b.address ? b : { ...b, address: addresses[`id:${b.id}`] }))
    .map((b) => {
      const change = moved[String(b.id)];
      if (!change) return b;
      pendingKeys.push(`moveBlock:${b.id}`);
      return { ...b, startIso: change.startIso, endIso: change.endIso };
    })
    .filter((b) => inRange(b.startIso));
  for (const added of state.local.blocks) {
    if (providerId !== undefined && added.providerId !== providerId) continue;
    if (!inRange(added.startIso)) continue;
    pendingKeys.push(`block:${added.tempId}`);
    blocks.push({ id: tempServerId(added.tempId), providerId: added.providerId, startIso: added.startIso, endIso: added.endIso, blockType: "meeting", visitTypeIds: null, title: added.title, address: added.address, canceled: false });
  }
  visits.sort((a, b) => a.startIso.localeCompare(b.startIso));
  blocks.sort((a, b) => a.startIso.localeCompare(b.startIso));
  return { visits, blocks, pendingKeys };
}

export function isPendingId(id: number): boolean {
  return id <= TEMP_ID_BASE;
}
